import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Ship, Plane, Award, FileCheck, ClipboardCheck, Package, Globe,
  Check, Loader2, ArrowRight, Download, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';
import useReveal from '@/hooks/useReveal';
import { apiClient, formatApiErrorDetail } from '@/lib/api';

const COUNTRIES = [
  {
    slug: 'my',
    flag: '🇲🇾',
    name: 'Malaysia',
    subtitle: 'Halal · JAKIM',
    eta: '5–7 days sea · 24 h air',
    port: 'Port Klang · KLIA',
    authority: 'JAKIM',
    compliance: [
      {
        icon: Award,
        title: 'JAKIM-recognised Halal certificate',
        body: "Every carton ships with a JAKIM-recognised Halal certificate issued by an Australian authority accepted under the JAKIM MOU (AFIC, ICCV or Halal Australia). Digitally signed originals sent with the airway bill.",
      },
      {
        icon: ClipboardCheck,
        title: 'DAFF export permit + Health Certificate',
        body: 'AQIS/DAFF-registered establishment. Government-signed Health Certificate issued for every consignment along with commercial invoice, packing list and BL/AWB.',
      },
      {
        icon: Package,
        title: 'Language + labelling',
        body: 'Bahasa Malaysia + English cartons. Halal logo, storage temperature and batch traceability code printed to your buyer\'s spec.',
      },
    ],
  },
  {
    slug: 'sg',
    flag: '🇸🇬',
    name: 'Singapore',
    subtitle: 'SFA · MUIS',
    eta: '4–6 days sea · 12 h air',
    port: 'PSA Singapore · Changi',
    authority: 'SFA & MUIS',
    compliance: [
      {
        icon: Award,
        title: 'SFA-approved sender',
        body: 'The Meat Works is an approved sender listed with the Singapore Food Agency. Every consignment ships with an SFA import permit reference from your appointed local importer.',
      },
      {
        icon: FileCheck,
        title: 'MUIS-recognised Halal',
        body: 'Halal certification recognised by MUIS. Certified end-to-end segregation from slaughter to packing in our Malaga cure room.',
      },
      {
        icon: Package,
        title: 'Retail-ready packaging',
        body: 'Cryovac primals or tray-pack for Singapore supermarket lines. Full traceability barcode + best-before dating to your buyer\'s SKU standard.',
      },
    ],
  },
  {
    slug: 'ae',
    flag: '🇦🇪',
    name: 'United Arab Emirates',
    subtitle: 'MOCCAE · ESMA · Musaffah',
    eta: '18–22 days sea · 24 h air',
    port: 'Jebel Ali · Musaffah Abu Dhabi · DXB',
    authority: 'MOCCAE',
    compliance: [
      {
        icon: Award,
        title: 'GCC-recognised Halal certificate',
        body: 'GCC Accreditation Center (GAC)-recognised Halal certificate on every carton. Approved by MOCCAE (UAE Ministry of Climate Change and Environment) for red meat and poultry imports.',
      },
      {
        icon: FileCheck,
        title: 'ESMA / Musaffah compliance',
        body: 'Meets ESMA product-standard requirements for import via Musaffah (Abu Dhabi) and Jebel Ali (Dubai). Consular authentication of documents available on request.',
      },
      {
        icon: Package,
        title: 'Arabic + English labelling',
        body: 'Dual-language cartons and shelf-ready packs. Country-of-origin, Halal mark, batch traceability and best-before printed to UAE municipality standard.',
      },
    ],
  },
];

const ETA_ROWS = [
  { destination: 'Kuala Lumpur (Port Klang)',      mode: 'Sea LCL',    days: '5–7 days',  freq: 'Weekly',       incoterms: 'FOB · CIF' },
  { destination: 'Kuala Lumpur (KLIA)',             mode: 'Air freight', days: '24 hours',  freq: 'Daily',        incoterms: 'CIF · DDP' },
  { destination: 'Singapore (PSA)',                 mode: 'Sea LCL',    days: '4–6 days',  freq: 'Weekly',       incoterms: 'FOB · CIF' },
  { destination: 'Singapore (Changi)',              mode: 'Air freight', days: '12 hours',  freq: 'Daily',        incoterms: 'CIF · DDP' },
  { destination: 'Dubai (Jebel Ali)',               mode: 'Sea FCL',    days: '18–22 days', freq: 'Fortnightly', incoterms: 'FOB · CIF' },
  { destination: 'Abu Dhabi (Musaffah)',            mode: 'Sea LCL',    days: '20–24 days', freq: 'Monthly',     incoterms: 'FOB · CIF' },
  { destination: 'Dubai (DXB airfreight)',          mode: 'Air freight', days: '24 hours',  freq: '3x weekly',   incoterms: 'CIF · DDP' },
];

const TRADE_SHEET_URL = `${process.env.REACT_APP_BACKEND_URL}/api/trade-sheet.pdf`;
const CATALOGUE_URL   = `${process.env.REACT_APP_BACKEND_URL}/api/catalogue.pdf`;

const CATALOGUE_LANGS = [
  { code: 'en', label: 'English',           short: 'EN' },
  { code: 'ms', label: 'Bahasa Malaysia',   short: 'BM' },
  { code: 'ar', label: 'العربية · Arabic',  short: 'AR' },
];


export default function Export() {
  const reveal = useReveal();
  const [tab, setTab] = useState('my');
  const [catLang, setCatLang] = useState('en');
  const active = COUNTRIES.find((c) => c.slug === tab);
  const catalogueHref = `${CATALOGUE_URL}?lang=${catLang}`;

  return (
    <div className="section-light App relative">
      <Seo
        title="Australian Halal Meat Export · Malaysia · Singapore · UAE"
        description="AQIS/DAFF export-registered Halal butchery. Premium Australian beef, lamb, poultry and smallgoods shipped to Kuala Lumpur, Port Klang, Singapore and Dubai. Full 118-product catalogue in English, Bahasa Malaysia and Arabic."
        keywords="halal meat export Malaysia, halal meat export Singapore, halal meat export UAE, Australian beef export Dubai, wagyu export Kuala Lumpur, AQIS DAFF meat exporter, cold chain meat shipping Perth"
        path="/export"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Halal meat export from Australia',
            serviceType: 'Meat export and cold-chain logistics',
            provider: { '@type': 'FoodEstablishment', '@id': 'https://themeatworks.com.au/#business' },
            areaServed: [
              { '@type': 'Country', name: 'Malaysia' },
              { '@type': 'Country', name: 'Singapore' },
              { '@type': 'Country', name: 'United Arab Emirates' },
              { '@type': 'Country', name: 'Australia' },
            ],
            audience: { '@type': 'BusinessAudience', audienceType: 'Importers, distributors, HoReCa operators' },
            description: 'AQIS/DAFF-registered export of Halal-certified Australian beef, lamb, poultry and smallgoods to Malaysia, Singapore and the UAE.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home',   item: 'https://themeatworks.com.au/' },
              { '@type': 'ListItem', position: 2, name: 'Export', item: 'https://themeatworks.com.au/export' },
            ],
          },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section
        id="top"
        data-testid="export-hero"
        className="relative pt-20 sm:pt-28 pb-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-[#FDFBF7] via-[#F7F3EC] to-[#FAFAFA]"
      >
        <div
          aria-hidden
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[680px] bg-[var(--mw-primary)]/[0.05] rounded-full blur-[140px]"
        />
        <div ref={reveal} className="mw-reveal relative max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <span className="block w-10 h-px bg-black/25" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-black/60">
              Export &middot; International Trade
            </span>
            <span className="block w-10 h-px bg-black/25" />
          </div>
          <h1
            data-testid="export-headline"
            className="font-display font-light leading-[0.98] tracking-tight text-black text-4xl sm:text-6xl lg:text-7xl xl:text-[6rem] max-w-5xl mx-auto"
          >
            Australian meat,
            <br className="hidden sm:block" />
            <em className="not-italic text-[var(--mw-primary)]">shipped world-class.</em>
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-black/70 text-base sm:text-lg leading-relaxed">
            MSA-graded and Halal-certified beef, lamb and poultry cleared for import into Malaysia,
            Singapore and the UAE — with government-signed documentation for every consignment.
          </p>

          {/* Country flags row */}
          <div
            data-testid="export-country-strip"
            className="mt-10 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
          >
            {['🇦🇺 Australia (origin)', '🇲🇾 Malaysia', '🇸🇬 Singapore', '🇦🇪 UAE'].map((f) => (
              <span
                key={f}
                className="inline-flex items-center gap-1.5 border border-black/15 bg-white/60 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-black/75"
              >
                {f}
              </span>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-3xl mx-auto">
            <a
              href="#enquire"
              onClick={(e) => { e.preventDefault(); document.getElementById('enquire')?.scrollIntoView({ behavior: 'smooth' }); }}
              data-testid="export-cta-enquire"
              style={{ color: '#fff' }}
              className="group flex-1 inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
            >
              Start an export enquiry
              <ArrowRight size={14} />
            </a>
            <a
              href={catalogueHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-cta-catalogue"
              className="group flex-1 inline-flex items-center justify-center gap-2 border border-black/25 hover:border-black text-black text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
            >
              <Download size={14} />
              Full catalogue (PDF)
            </a>
            <a
              href={TRADE_SHEET_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-cta-download"
              className="group flex-1 inline-flex items-center justify-center gap-2 border border-black/25 hover:border-black text-black text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
            >
              <Download size={14} />
              Trade sheet (PDF)
            </a>
          </div>

          {/* Catalogue language toggle */}
          <div
            data-testid="export-catalogue-lang"
            className="mt-6 flex flex-col sm:flex-row sm:items-center justify-center gap-3"
          >
            <span className="text-[10px] uppercase tracking-[0.28em] text-black/45">
              Catalogue language
            </span>
            <div className="inline-flex items-center gap-1.5 p-1 border border-black/15 bg-white">
              {CATALOGUE_LANGS.map((L) => {
                const on = catLang === L.code;
                return (
                  <button
                    key={L.code}
                    type="button"
                    onClick={() => setCatLang(L.code)}
                    data-testid={`export-catalogue-lang-${L.code}`}
                    aria-pressed={on}
                    style={on ? { color: '#fff' } : {}}
                    className={`text-[11px] uppercase tracking-[0.22em] px-3.5 py-2 transition-colors ${
                      on
                        ? 'bg-[var(--mw-primary)]'
                        : 'text-black/70 hover:text-black hover:bg-black/[0.04]'
                    }`}
                  >
                    <span className="hidden sm:inline">{L.label}</span>
                    <span className="sm:hidden">{L.short}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-black/45">
            Full 118-product catalogue &middot; ~18 MB PDF &middot; MSA-graded &amp; Halal-certified cuts
          </p>
        </div>
      </section>

      {/* Compliance tabs */}
      <section
        id="compliance"
        data-testid="export-compliance"
        className="py-20 sm:py-28 bg-white border-t border-black/5"
      >
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
              Country compliance
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[0.98] tracking-tight text-black">
              Documented for <em className="not-italic text-[var(--mw-primary)]">every jurisdiction</em>.
            </h2>
            <p className="mt-6 text-black/65 leading-relaxed">
              We hold the certifications that clear customs on the ground. Choose a destination to see
              what ships with every consignment.
            </p>
          </div>

          {/* Tabs */}
          <div
            role="tablist"
            data-testid="export-tabs"
            className="flex flex-wrap gap-2 justify-center mb-10"
          >
            {COUNTRIES.map((c) => {
              const on = tab === c.slug;
              return (
                <button
                  key={c.slug}
                  role="tab"
                  aria-selected={on}
                  onClick={() => setTab(c.slug)}
                  data-testid={`export-tab-${c.slug}`}
                  style={on ? { color: '#fff' } : {}}
                  className={`inline-flex items-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                    on
                      ? 'bg-[var(--mw-primary)] border-[var(--mw-primary)]'
                      : 'bg-white border-black/15 text-black/65 hover:border-black/40 hover:text-black'
                  }`}
                >
                  <span className="text-lg leading-none">{c.flag}</span>
                  {c.name}
                  <span className={on ? 'text-white/70' : 'text-black/40'}>· {c.subtitle}</span>
                </button>
              );
            })}
          </div>

          {/* Active country panel */}
          <div
            key={active.slug}
            data-testid={`export-panel-${active.slug}`}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {active.compliance.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex flex-col gap-4 bg-white border border-black/10 p-6 sm:p-7 hover:border-[var(--mw-primary)] transition-colors shadow-[0_12px_40px_-24px_rgba(0,0,0,0.15)]"
                >
                  <Icon size={22} className="text-[var(--mw-primary)]" />
                  <div className="font-display text-lg sm:text-xl text-black leading-tight tracking-tight">
                    {item.title}
                  </div>
                  <p className="text-sm text-black/65 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Quick facts row */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <QuickFact label="Certifying authority" value={active.authority} />
            <QuickFact label="Ports served" value={active.port} />
            <QuickFact label="Typical ETA" value={active.eta} />
          </div>
        </div>
      </section>

      {/* Shipping ETAs table */}
      <section
        id="shipping"
        data-testid="export-shipping"
        className="py-20 sm:py-28 bg-[#FAFAFA] border-t border-black/5"
      >
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
              Shipping &middot; ETAs
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[0.98] tracking-tight text-black">
              Ex-Perth to your <em className="not-italic text-[var(--mw-primary)]">dock or store</em>.
            </h2>
            <p className="mt-6 text-black/65 leading-relaxed">
              Chilled sea LCL/FCL and daily air freight to key ASEAN and GCC ports.
              Incoterms tailored to your import terms.
            </p>
          </div>

          <div className="border border-black/10 bg-white overflow-x-auto">
            <table
              data-testid="export-eta-table"
              className="w-full text-left text-sm text-black"
            >
              <thead className="bg-black/[0.03] text-[10px] uppercase tracking-[0.22em] text-black/55">
                <tr>
                  <th className="px-5 py-4 font-normal">Destination</th>
                  <th className="px-5 py-4 font-normal">Mode</th>
                  <th className="px-5 py-4 font-normal">Transit</th>
                  <th className="px-5 py-4 font-normal">Frequency</th>
                  <th className="px-5 py-4 font-normal">Incoterms</th>
                </tr>
              </thead>
              <tbody>
                {ETA_ROWS.map((r) => {
                  const isAir = r.mode.toLowerCase().includes('air');
                  const Icon = isAir ? Plane : Ship;
                  return (
                    <tr key={r.destination} className="border-t border-black/5 hover:bg-black/[0.015] transition-colors">
                      <td className="px-5 py-4 font-display text-base">{r.destination}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] ${isAir ? 'text-sky-600' : 'text-emerald-600'}`}>
                          <Icon size={12} /> {r.mode}
                        </span>
                      </td>
                      <td className="px-5 py-4 tabular-nums">{r.days}</td>
                      <td className="px-5 py-4 text-black/60">{r.freq}</td>
                      <td className="px-5 py-4 text-[10px] uppercase tracking-[0.22em] text-black/60">{r.incoterms}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex items-start gap-2 text-xs text-black/50">
            <Info size={14} className="mt-0.5 shrink-0" />
            Transit times ex-Perth (Fremantle Port / PER airport). Reefer container temperatures held
            at −18°C frozen or 4°C chilled throughout transit. Custom clearance handled by your appointed
            forwarder or ours on request.
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <ExportEnquirySection />

      <Footer />
    </div>
  );
}


function QuickFact({ label, value }) {
  return (
    <div className="border border-black/10 bg-white p-5">
      <div className="text-[10px] uppercase tracking-[0.24em] text-black/50">{label}</div>
      <div className="mt-2 font-display text-lg leading-tight text-black">{value}</div>
    </div>
  );
}


function ExportEnquirySection() {
  const [form, setForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    destination: 'my',
    volume: '',
    products: '',
    incoterms: 'FOB',
    packaging: 'cryovac',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const country = COUNTRIES.find((c) => c.slug === form.destination);
      const message = [
        `EXPORT ENQUIRY — ${country?.name || form.destination.toUpperCase()}`,
        '',
        `Destination:     ${country?.name} (${country?.slug.toUpperCase()})`,
        `Volume / freq.:  ${form.volume}`,
        `Products:        ${form.products}`,
        `Incoterms:       ${form.incoterms}`,
        `Packaging:       ${form.packaging}`,
        '',
        'Notes:',
        form.notes || '(none)',
      ].join('\n');
      await apiClient.post('/inquiries', {
        business_name: form.business_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        categories: ['Export'],
        estimated_volume: form.volume,
        message,
        preferred_lang: catLang,
      });
      setSubmitted(true);
      toast.success('Export enquiry received — we\'ll be in touch within 1 business day.');
      window.scrollTo({ top: document.getElementById('enquire').offsetTop - 60, behavior: 'smooth' });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || 'Submission failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="enquire" className="py-24 bg-white border-t border-black/5 scroll-mt-24">
        <div className="max-w-2xl mx-auto px-6 sm:px-10 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-6">
            <Check size={28} className="text-emerald-600" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
            Enquiry received
          </div>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-tight tracking-tight text-black mb-4">
            Passport-stamped.
          </h2>
          <p className="text-black/65 leading-relaxed">
            An export specialist will be in touch within one business day with pricing, documentation
            samples and a fulfilment timeline for your destination.
          </p>
          <Link
            to="/"
            style={{ color: '#fff' }}
            className="mt-10 inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-xs uppercase tracking-[0.22em] px-6 py-3 transition-colors"
          >
            Return to homepage
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      id="enquire"
      data-testid="export-enquire"
      className="py-20 sm:py-28 bg-white border-t border-black/5 scroll-mt-24"
    >
      <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
            Export enquiry
          </div>
          <h2 className="font-display font-light text-4xl sm:text-5xl leading-[0.98] tracking-tight text-black">
            Tell us about your <em className="not-italic text-[var(--mw-primary)]">market</em>.
          </h2>
          <p className="mt-5 text-black/65 leading-relaxed max-w-xl mx-auto">
            Destination, volume, products and incoterms. We&apos;ll come back with pricing and a
            documentation sample within one business day.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="export-form"
          className="bg-white border border-black/10 p-6 sm:p-10 space-y-5 shadow-[0_12px_40px_-24px_rgba(0,0,0,0.15)]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Business / trading name *">
              <input required minLength={2} maxLength={200} value={form.business_name} onChange={set('business_name')} className="mw-input" data-testid="export-business-name" />
            </F>
            <F label="Contact name *">
              <input required minLength={2} maxLength={120} value={form.contact_name} onChange={set('contact_name')} className="mw-input" data-testid="export-contact-name" />
            </F>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Email *">
              <input required type="email" value={form.email} onChange={set('email')} className="mw-input" data-testid="export-email" />
            </F>
            <F label="Phone / WhatsApp *">
              <input required minLength={4} value={form.phone} onChange={set('phone')} className="mw-input" data-testid="export-phone" placeholder="+60 12 345 6789" />
            </F>
          </div>

          <F label="Destination market *">
            <div className="grid grid-cols-3 gap-2">
              {COUNTRIES.map((c) => {
                const on = form.destination === c.slug;
                return (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, destination: c.slug }))}
                    data-testid={`export-dest-${c.slug}`}
                    className={`text-left p-3 border-2 transition-colors ${on ? 'border-[var(--mw-primary)] bg-white' : 'border-black/10 bg-white hover:border-black/30'}`}
                  >
                    <div className="text-2xl leading-none mb-1">{c.flag}</div>
                    <div className="font-display text-sm text-black">{c.name}</div>
                    <div className="text-[9px] uppercase tracking-[0.16em] text-black/50 mt-1">{c.subtitle}</div>
                  </button>
                );
              })}
            </div>
          </F>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Estimated volume / frequency *">
              <input required maxLength={120} value={form.volume} onChange={set('volume')} className="mw-input" placeholder="e.g. 1 x 20ft reefer / month" data-testid="export-volume" />
            </F>
            <F label="Products of interest *">
              <input required maxLength={200} value={form.products} onChange={set('products')} className="mw-input" placeholder="Beef primals, lamb racks, chicken MDM…" data-testid="export-products" />
            </F>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Preferred incoterms">
              <select value={form.incoterms} onChange={set('incoterms')} className="mw-input cursor-pointer" data-testid="export-incoterms">
                {['FOB', 'CIF', 'CFR', 'DDP', 'EXW', 'Not sure — advise'].map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </F>
            <F label="Packaging">
              <select value={form.packaging} onChange={set('packaging')} className="mw-input cursor-pointer" data-testid="export-packaging">
                <option value="cryovac">Cryovac primals</option>
                <option value="tray">Retail tray-pack</option>
                <option value="carton">Master carton bulk</option>
                <option value="mixed">Mixed / advise</option>
              </select>
            </F>
          </div>

          <F label="Anything else? (importer info, certificates, HS codes…)">
            <textarea rows={4} maxLength={1500} value={form.notes} onChange={set('notes')} className="mw-input resize-none" data-testid="export-notes" />
          </F>

          {error && (
            <div className="border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm" data-testid="export-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="export-submit"
            style={{ color: '#fff' }}
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] disabled:opacity-50 text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
          >
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : (
              <>
                <Globe size={14} /> Send export enquiry
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}


function F({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.22em] text-black/55 mb-2">{label}</div>
      {children}
    </label>
  );
}
